/**
 * Adjustment Draft Processor
 * 处理单个 schedule 的调整数据，转换为标准 AuditAdjustLine 格式
 */
import type {AuditAdjustLine} from '@/services/auditService/auditAdjustOrchestrate';
import type {CommonRow} from '@/services/auditService/interface/wp-row';
import type {CommonInvestmentWorkingPaper} from '@/services/auditService/interface/common';
import {extractRowsDetails, calculateAmount} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/working-helpers';
import {getAdjustmentTotal} from '@/pages/components/audit/adjustment/adjust/utils/adjustmentLockUtils';
import {resolveFlatEquityAdjustment, resolveFlatEquityRenderEntry} from '@/pages/components/audit/adjustment/adjust/utils/classifyRows';
import {
    isPpeFieldSyntheticDetail,
    isPpeCategoryRowBoundDetail,
    toPpeFieldAdjSubmitDetail,
    collectNestedPpeFieldAdjScalars,
    PPE_FIELD_ADJ_KEYS,
    PPE_ROW_ARRAY_KEYS,
    PPE_NBV_SPLIT_FLAG,
    isIaFieldSyntheticDetail,
    isItlDepIaFieldTabsNonDtdDetail,
    toIaFieldAdjSubmitDetail,
    IA_FIELD_ADJ_KEYS,
    iaOwnedFieldKey,
} from '@/pages/components/audit/adjustment/adjust/utils/consolidateWpAdjustments';
import {buildAdjustmentLine} from '@/adjustment/adapters';
import {useFsAdjustStore} from '@/store/audit/fsAdjustStore';
import {resolveFromPaperAdjustmentFields, resolveFsNodeOriginalEntry} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/adjustment-helpers';

export interface ProcessScheduleParams {
    code: string;
    detail: any;
    annualId: number;
    entry?: string;  // effective entry (Dr/Cr)，直接来自 AdjustDrCrView 渲染列
}

/**
 * 处理单个 schedule 的调整数据
 * @returns AuditAdjustLine 或 null（如果无法处理）
 */
export function processScheduleToLine(params: ProcessScheduleParams): AuditAdjustLine | null {
    const { code, detail, annualId, entry: originalEntry } = params;
    const fsAdjustState = useFsAdjustStore.getState();
    const line = fsAdjustState.getLineByCode(code);
    
    if (!line) {
        console.warn('[processScheduleToLine] No line found for code:', code);
        return null;
    }

    // 优先使用传入的 entry，否则从 line 中获取
    const baseEntry = originalEntry || line.entry || 'Dr';

    // 类型判断
    const isInvestment = ['BS-NCA-ISUB', 'BS-NCA-IASS', 'BS-CA-IOT'].includes(code);
    const isBankType = code === 'BS-CL-BOD' || code === 'BS-CA-CASH';
    const isSalesIncome = code === 'PL-TURN-SI' || code === 'PL-TURN-SE';
    const isPurchases = code === 'PL-COT-PUR';
    const isLoan = code === 'BS-CL-BLS' || code === 'BS-CL-OFL' || code === 'BS-NCL-BLS' || code === 'BS-NCL-OFL';
    // Interest field WP→WP: code=PL-FC-LI with loan arrays, or interestSummary + top-level adjustment
    // (InterestSummaryWp GET 仍可能无 bankLoans；提交 toPaper/buildSubmitHeader 会转为 loan 执行态)
    const hasLoanArrays = Array.isArray(detail?.bankLoans) || Array.isArray(detail?.vehicleLoans);
    const hasInterestSummaryAdj = detail?.interestSummary != null
        && typeof detail.interestSummary === 'object'
        && Number.isFinite(Number(detail?.adjustment))
        && Math.abs(Number(detail.adjustment)) > 0.001;
    const isLoanFieldInterest = code === 'PL-FC-LI' && (hasLoanArrays || hasInterestSummaryAdj);
    // Rental field WP→WP only: details = { rows, rentalOtherInfo, adjustment } with top-level adjustment
    const isRentalFieldWpToWp = code === 'PL-AE-REN'
        && Array.isArray(detail?.rows)
        && Number.isFinite(Number(detail?.adjustment))
        && Math.abs(Number(detail.adjustment)) > 0.001;
    const isInterestIncome = code === 'PL-OI-II' || code === 'PL-OI-SFG';
    const isDonation = code === 'PL-AE-DON';
    const isCommissionPayment = code === 'PL-AE-COM';
    const isGainLossExchange = code === 'PL-OI-EXG' || code === 'PL-AE-EXL';
    const isGainLossDisposal = code === 'PL-OI-GOD' || code === 'PL-AE-LOD';
    const isDueFromToDirectors = code === 'BS-CA-DIR' || code === 'BS-CL-DTD';

    let adjustmentLine: any = null;

    if (isBankType) {
        adjustmentLine = processBankType(code, detail, line, annualId, baseEntry);
    } else if (isInvestment) {
        adjustmentLine = processInvestment(code, detail, line, annualId, baseEntry);
    } else if (isSalesIncome || isPurchases) {
        adjustmentLine = processSalesOrPurchases(code, detail, line, annualId, isSalesIncome, baseEntry);
    } else if (isLoan || isLoanFieldInterest) {
        adjustmentLine = processLoan(code, detail, line, annualId, baseEntry);
    } else if (isRentalFieldWpToWp) {
        adjustmentLine = processRentalExpense(code, detail, line, annualId, baseEntry);
    } else if (isPpeCategoryRowBoundDetail(detail)) {
        // FsSelectionDrawer PPE 分类 toPaper：保留完整 WP，按 xxxRow.adjustment 汇总金额
        adjustmentLine = processPpeCategoryAdjustment(code, detail, line, annualId, baseEntry);
    } else if (isPpeFieldSyntheticDetail(detail, code)) {
        // PPE field WP→WP: draft uses top-level markers; submit nests via toPpeFieldAdjSubmitDetail
        adjustmentLine = processPpeFieldAdjustment(code, detail, line, annualId, baseEntry);
    } else if (
        isIaFieldSyntheticDetail(detail, code)
        || isItlDepIaFieldTabsNonDtdDetail(detail, code)
    ) {
        // IA/AIA/ITL field WP→WP: slim amorChgAdj|nbvCfAdj — do not fall through to processDefault
        adjustmentLine = processIaFieldAdjustment(code, detail, line, annualId, baseEntry);
    } else if (isInterestIncome) {
        adjustmentLine = processInterestIncome(code, detail, line, annualId, baseEntry);
    } else if (isDonation) {
        adjustmentLine = processDonation(code, detail, line, annualId, baseEntry);
    } else if (isCommissionPayment) {
        adjustmentLine = processCommissionPayment(code, detail, line, annualId, baseEntry);
    } else if (isGainLossExchange) {
        adjustmentLine = processGainLossExchange(code, detail, line, annualId, baseEntry);
    } else if (isGainLossDisposal) {
        adjustmentLine = processGainLossDisposal(code, detail, line, annualId, baseEntry);
    } else if (isDueFromToDirectors) {
        adjustmentLine = processDueFromToDirectors(code, detail, line, annualId, baseEntry);
    } else if ((code === 'BS-EQ-DIV' || code === 'BS-EQ-AL')
        && !(Array.isArray(detail?.commonRow) && detail.commonRow.length > 0)) {
        // BS-EQ-DIV / BS-EQ-AL flat：仅无非空 commonRow 时走 flat；
        // DIV toPaper / AL CommonWp 带 commonRow 时走下方 processDefault
        const rawAdjustment = resolveFlatEquityAdjustment(detail);
        const renderEntry = resolveFlatEquityRenderEntry(originalEntry, line.entry || 'Cr', rawAdjustment);
        adjustmentLine = buildAdjustmentLine({
            code,
            detailsPayload: {
                perClient: Number(detail?.perClient) || 0,
                adjustment: rawAdjustment,
            },
            categoryLine: line as any,
            companyId: annualId,
            annualId,
            statement: ((line as any).statement || 'BS') as 'BS' | 'PL',
            currency: 'HKD',
            totalAdjustment: Math.abs(rawAdjustment),
            totalFinal: Math.abs(rawAdjustment),
            entry: renderEntry,
        });
    } else if (code === 'BS-EQ-IC' && detail && typeof detail === 'object'
        && (Array.isArray(detail.shareholders)
            || Array.isArray(detail.directors)
            || detail.registeredInfo != null
            || detail.holdingCompany != null
            || ('adjustment' in detail))) {
        // Issued Capital：有 WP bag 或 schedule-level adjustment 时勿走 processDefault（会误扫 shareholders）
        // 非 DTD：adjustmentAmount = |details.adjustment|，entry 用传入的渲染列
        const rawAdjustment = Number(detail?.adjustment) || 0;
        const renderEntry = (baseEntry === 'Dr' || baseEntry === 'Cr')
            ? baseEntry
            : resolveFlatEquityRenderEntry(originalEntry, line.entry || 'Cr', rawAdjustment);
        adjustmentLine = buildAdjustmentLine({
            code,
            detailsPayload: detail,
            categoryLine: line as any,
            companyId: annualId,
            annualId,
            statement: ((line as any).statement || 'BS') as 'BS' | 'PL',
            currency: 'HKD',
            totalAdjustment: Math.abs(rawAdjustment),
            totalFinal: Math.abs(rawAdjustment),
            entry: renderEntry,
        });
    } else {
        adjustmentLine = processDefault(code, detail, line, annualId, baseEntry);
    }

    return adjustmentLine as AuditAdjustLine | null;
}

// ========== 类型处理函数 ==========

function processBankType(code: string, detail: any, line: any, annualId: number, baseEntry: string) {
    const bankData = extractRowsDetails(detail, undefined, 'bank') as any;
    const bankRows = bankData?.bankRow || [];
    const commonRows = bankData?.commonRow || [];
    
    const bankRowsAsCommon: CommonRow[] = bankRows.map((br: any) => ({
        id: br.bankId,
        item: '',
        perClient: br.perClient,
        adjustment: br.adjustment,
        lastYearFinal: br.lastYearFinal
    }));
    
    const allRows = [...bankRowsAsCommon, ...commonRows];
    
    if (allRows.length === 0) {
        return null;
    }
    
    const amountDifference = calculateAmount(allRows);
    const totalFinal = allRows.reduce((sum: number, row: CommonRow) => 
        sum + (Number(row.perClient) || 0) + getAdjustmentTotal(row), 0);
    
    return buildAdjustmentLine({
        code,
        detailsPayload: detail,
        categoryLine: line,
        companyId: annualId,
        annualId,
        statement: line.statement as 'BS' | 'PL',
        currency: 'HKD',
        totalAdjustment: Math.abs(amountDifference),
        totalFinal,
        entry: baseEntry,
    });
}

function processInvestment(code: string, detail: any, line: any, annualId: number, baseEntry: string) {
    const wp = extractRowsDetails(detail, undefined, 'commonInvestment') as CommonInvestmentWorkingPaper;
    const costRows = wp?.costRow || [];
    const impairmentRows = wp?.impairmentLossRow || [];
    
    if (costRows.length === 0 && impairmentRows.length === 0) return null;

    const amountDifference = calculateAmount(costRows) + calculateAmount(impairmentRows);
    const totalFinal = [...costRows, ...impairmentRows]
        .reduce((sum: number, row: CommonRow) => sum + (Number(row.perClient) || 0) + getAdjustmentTotal(row), 0);

    // 直接使用原始 detail 数据
    return buildAdjustmentLine({
        code,
        detailsPayload: detail,
        categoryLine: line,
        companyId: annualId,
        annualId,
        statement: line.statement as 'BS' | 'PL',
        currency: 'HKD',
        totalAdjustment: Math.abs(amountDifference),
        totalFinal,
        entry: baseEntry,
    });
}

function processSalesOrPurchases(code: string, detail: any, line: any, annualId: number, isSalesIncome: boolean, baseEntry: string) {
    const strategyType = isSalesIncome ? 'salesIncome' : 'purchases';
    const wp = extractRowsDetails(detail, undefined, strategyType) as any;
    const mainRows = isSalesIncome ? (wp?.salesIncome || []) : (wp?.purchases || []);

    const amountDifference = calculateAmount(mainRows);
    const totalFinal = mainRows.reduce((sum: number, row: CommonRow) => 
        sum + (Number(row.perClient) || 0) + getAdjustmentTotal(row), 0);
    
    return buildAdjustmentLine({
        code,
        detailsPayload: detail,
        categoryLine: line,
        companyId: annualId,
        annualId,
        statement: line.statement as 'BS' | 'PL',
        currency: 'HKD',
        totalAdjustment: Math.abs(amountDifference),
        totalFinal,
        entry: baseEntry,
    });
}

function processLoan(code: string, detail: any, line: any, annualId: number, baseEntry: string) {
    const loanData = extractRowsDetails(detail, undefined, 'loan', code) as any;
    const bankLoans = loanData?.bankLoans || [];
    const vehicleLoans = loanData?.vehicleLoans || [];
    const allLoans = [...bankLoans, ...vehicleLoans];

    // Field-level WP→WP: amount lives on details.adjustment (sibling of bankLoans/vehicleLoans)
    const topLevelAdj = Number(
        detail?.adjustment
        ?? loanData?.adjustment
        ?? 0,
    );
    const hasTopLevelAdj = Number.isFinite(topLevelAdj) && Math.abs(topLevelAdj) > 0.001;

    const loanRowsAsCommon: CommonRow[] = allLoans.map((loan: any) => ({
        id: loan.id,
        item: '',
        perClient: loan.perClient,
        adjustment: Number(loan.adjustment) || 0,
        adjustments: Array.isArray(loan.adjustments) && !('currAdj' in (loan.adjustments || {}))
            ? loan.adjustments
            : null,
        lastYearFinal: loan.lastYearFinal,
    }));

    if (allLoans.length === 0 && !hasTopLevelAdj) {
        return null;
    }

    const rowAdjTotal = calculateAmount(loanRowsAsCommon);
    const amountDifference = hasTopLevelAdj ? topLevelAdj : rowAdjTotal;
    const totalFinal = loanRowsAsCommon.reduce((sum: number, row: CommonRow) =>
        sum + (Number(row.perClient) || 0) + getAdjustmentTotal(row), 0);

    // Preserve top-level adjustment on details for backend attr creation
    const detailsPayload = hasTopLevelAdj
        ? {...(detail || loanData || {}), adjustment: topLevelAdj}
        : (detail || loanData);

    return buildAdjustmentLine({
        code,
        detailsPayload,
        categoryLine: line,
        companyId: annualId,
        annualId,
        statement: line.statement as 'BS' | 'PL',
        currency: 'HKD',
        totalAdjustment: Math.abs(amountDifference),
        totalFinal,
        entry: baseEntry,
    });
}

/**
 * PL-AE-REN field WP→WP: amount lives on details.adjustment (sibling of rows / rentalOtherInfo).
 * line.adjustmentAmount = abs(details.adjustment); entry follows non-DTD FS rules
 * (originalEntry + signed adjustment → render column), same as loan field WP→WP.
 */
function processRentalExpense(code: string, detail: any, line: any, annualId: number, baseEntry: string) {
    const rentalData = extractRowsDetails(detail, undefined, 'rentalExpenses') as any;
    const rows = Array.isArray(detail?.rows)
        ? detail.rows
        : (Array.isArray(rentalData?.rows) ? rentalData.rows : []);

    const topLevelAdj = Number(detail?.adjustment ?? rentalData?.adjustment ?? 0);
    const hasTopLevelAdj = Number.isFinite(topLevelAdj) && Math.abs(topLevelAdj) > 0.001;

    if (rows.length === 0 && !hasTopLevelAdj) {
        return null;
    }

    const originalEntry = resolveFsNodeOriginalEntry(code, line?.entry) || line?.entry || 'Dr';
    // Prefer AdjustDrCrView render entry when provided; otherwise resolve from signed adj (non-DTD)
    const resolved = resolveFromPaperAdjustmentFields(originalEntry, hasTopLevelAdj ? topLevelAdj : 0, code);
    const entry = (baseEntry === 'Dr' || baseEntry === 'Cr') ? baseEntry : resolved.entry;
    const signedAdj = hasTopLevelAdj ? topLevelAdj : resolved.adjustment;

    const detailsPayload = hasTopLevelAdj
        ? {...(detail || rentalData || {}), adjustment: signedAdj}
        : (detail || rentalData);

    return buildAdjustmentLine({
        code,
        detailsPayload,
        categoryLine: line,
        companyId: annualId,
        annualId,
        statement: line.statement as 'BS' | 'PL',
        currency: 'HKD',
        totalAdjustment: Math.abs(signedAdj),
        totalFinal: Math.abs(signedAdj),
        entry,
    });
}

/**
 * PPE category toPaper 提交形态：保留完整 xxxRow 壳（含 []），与 GET / 公共公式一致。
 * 空 Row 省略仅限 PropertyPlantEquipment 组件 Save / 空分类 field toPaper（nestPpeFieldAdjOntoTargetRow）。
 */
export function toPpeCategorySubmitDetail(detail: any): Record<string, any> {
    const source = detail && typeof detail === 'object' && !Array.isArray(detail) ? detail : {};
    const next: Record<string, any> = {...source};
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = next[rowKey];
        if (!Array.isArray(rows)) {
            next[rowKey] = [];
            continue;
        }
        next[rowKey] = rows.map((row) => {
            if (!row || typeof row !== 'object') return row;
            // adjustments = render-only; keep scalar row.adjustment for category toPaper
            const {adjustments: _adj, ...rest} = row as Record<string, unknown>;
            return {...rest};
        });
    }
    if (!Array.isArray(next.testRow)) {
        next.testRow = [];
    }
    if ('adjustments' in next) {
        delete next.adjustments;
    }
    if (!next.ppeRate || typeof next.ppeRate !== 'object' || Array.isArray(next.ppeRate)) {
        next.ppeRate = {
            locationOfPropertyRate: 4,
            commercialAllowanceRate: 20,
            industrialAllowanceRate: 20,
            furnitureFixtureRate: 20,
            computerHardSoftRate: 20,
            fullPayMotorRate: 30,
            hirePurchaseMotorRate: 30,
            machineryRate: 20,
            liftEscalatorRate: 20,
            equipmentRate: 20,
            equipmentIrdRate: 20,
        };
    }
    return next;
}

/**
 * PPE category toPaper (FsSelectionDrawer xxxRow)：提交完整 WP shell，
 * line.adjustmentAmount = Σ row.adjustment（仅当前分类写入）。
 */
function processPpeCategoryAdjustment(code: string, detail: any, line: any, annualId: number, baseEntry: string) {
    const detailsPayload = toPpeCategorySubmitDetail(detail);
    let totalAdjustment = 0;
    for (const rowKey of PPE_ROW_ARRAY_KEYS) {
        const rows = detailsPayload[rowKey];
        if (!Array.isArray(rows)) continue;
        for (const row of rows) {
            if (!row || typeof row !== 'object') continue;
            totalAdjustment += Number((row as any).adjustment) || 0;
        }
    }

    return buildAdjustmentLine({
        code,
        detailsPayload,
        categoryLine: line,
        companyId: annualId,
        annualId,
        statement: line.statement as 'BS' | 'PL',
        currency: 'HKD',
        totalAdjustment: Math.abs(totalAdjustment),
        totalFinal: Math.abs(totalAdjustment),
        entry: baseEntry,
    });
}

/**
 * PPE field WP→WP: PropertyPlantEquipment save-shaped details + field markers.
 * Submit via {@link toPpeFieldAdjSubmitDetail} nests onto xxxRow (API outbound only).
 * Amount from detail.adjustment, nested row field keys, or line.adjustmentAmount.
 */
function processPpeFieldAdjustment(code: string, detail: any, line: any, annualId: number, baseEntry: string) {
    const nested = collectNestedPpeFieldAdjScalars(detail);
    let nestedSum = 0;
    for (const k of PPE_FIELD_ADJ_KEYS) {
        const fromRow = Number(nested.fieldSums[k]) || 0;
        const fromTop = Number(detail?.[k]) || 0;
        nestedSum += Math.abs(fromRow) >= 0.001 ? fromRow : fromTop;
    }

    const topLevelAdj = Number(detail?.adjustment ?? 0);
    const hasTopLevelAdj = Number.isFinite(topLevelAdj) && Math.abs(topLevelAdj) > 0.001;
    const lineSigned = Number(line?.adjustment);
    const hasLineSigned = Number.isFinite(lineSigned) && Math.abs(lineSigned) >= 0.001;
    const signedAdj = hasTopLevelAdj
        ? topLevelAdj
        : (Math.abs(nestedSum) >= 0.001
            ? nestedSum
            : (hasLineSigned ? lineSigned : Number(line?.adjustmentAmount ?? 0)));
    if (Math.abs(signedAdj) < 0.001) {
        return null;
    }

    const originalEntry = resolveFsNodeOriginalEntry(code, line?.entry) || line?.entry || 'Dr';
    const resolved = resolveFromPaperAdjustmentFields(originalEntry, signedAdj, code);
    const entry = (baseEntry === 'Dr' || baseEntry === 'Cr') ? baseEntry : resolved.entry;

    const detailsPayload = toPpeFieldAdjSubmitDetail(detail, signedAdj, code);
    delete detailsPayload[PPE_NBV_SPLIT_FLAG];

    return buildAdjustmentLine({
        code,
        detailsPayload,
        categoryLine: line,
        companyId: annualId,
        annualId,
        statement: line.statement as 'BS' | 'PL',
        currency: 'HKD',
        totalAdjustment: Math.abs(signedAdj),
        totalFinal: Math.abs(signedAdj),
        entry,
    });
}

/**
 * IA / AIA / ITL field WP→WP: slim {intangibleRowId?, amorChgAdj|nbvCfAdj, adjustment}.
 * Mirrors processPpeFieldAdjustment — without this, processDefault scans empty rows → amount 0.
 */
function processIaFieldAdjustment(code: string, detail: any, line: any, annualId: number, baseEntry: string) {
    const ownedKey = iaOwnedFieldKey(code);
    let fieldSum = 0;
    for (const k of IA_FIELD_ADJ_KEYS) {
        if (k !== ownedKey) continue;
        const top = Number(detail?.[k]) || 0;
        if (Math.abs(top) >= 0.001) fieldSum += top;
    }

    const topLevelAdj = Number(detail?.adjustment ?? 0);
    const hasTopLevelAdj = Number.isFinite(topLevelAdj) && Math.abs(topLevelAdj) > 0.001;
    const lineSigned = Number(line?.adjustment);
    const hasLineSigned = Number.isFinite(lineSigned) && Math.abs(lineSigned) >= 0.001;
    const signedAdj = hasTopLevelAdj
        ? topLevelAdj
        : (Math.abs(fieldSum) >= 0.001
            ? fieldSum
            : (hasLineSigned ? lineSigned : Number(line?.adjustmentAmount ?? 0)));
    if (Math.abs(signedAdj) < 0.001) {
        return null;
    }

    const originalEntry = resolveFsNodeOriginalEntry(code, line?.entry) || line?.entry || 'Dr';
    const resolved = resolveFromPaperAdjustmentFields(originalEntry, signedAdj, code);
    const entry = (baseEntry === 'Dr' || baseEntry === 'Cr') ? baseEntry : resolved.entry;

    return buildAdjustmentLine({
        code,
        detailsPayload: toIaFieldAdjSubmitDetail(detail, signedAdj, code),
        categoryLine: line,
        companyId: annualId,
        annualId,
        statement: line.statement as 'BS' | 'PL',
        currency: 'HKD',
        totalAdjustment: Math.abs(signedAdj),
        totalFinal: Math.abs(signedAdj),
        entry,
    });
}

function processInterestIncome(code: string, detail: any, line: any, annualId: number, baseEntry: string) {
    const interestData = extractRowsDetails(detail, undefined, 'interestIncome') as any;
    const bankInterest = interestData?.bankInterestIncome || [];
    const otherInterest = interestData?.otherInterestIncome || [];
    const allInterest = [...bankInterest, ...otherInterest];
    
    const interestRowsAsCommon: CommonRow[] = allInterest.map((interest: any) => ({
        id: interest.id,
        item: '',
        perClient: interest.perClient,
        adjustment: interest.adjustment,
        adjustments: interest.adjustments,
        lastYearFinal: interest.lastYearFinal
    }));
    
    if (allInterest.length === 0) {
        return null;
    }
    
    const amountDifference = calculateAmount(interestRowsAsCommon);
    const totalFinal = interestRowsAsCommon.reduce((sum: number, row: CommonRow) => {
        const adj = Array.isArray(row.adjustments) && row.adjustments.length > 0
            ? row.adjustments.reduce((s: number, d: any) => s + (Number(d?.adjustment) || 0), 0)
            : 0;
        return sum + (Number(row.perClient) || 0) + adj;
    }, 0);
    
    return buildAdjustmentLine({
        code,
        detailsPayload: detail,
        categoryLine: line,
        companyId: annualId,
        annualId,
        statement: line.statement as 'BS' | 'PL',
        currency: 'HKD',
        totalAdjustment: Math.abs(amountDifference),
        totalFinal,
        entry: baseEntry,
    });
}

function processDonation(code: string, detail: any, line: any, annualId: number, baseEntry: string) {
    const donationData = extractRowsDetails(detail, undefined, 'donation') as any;
    const taxDeductible = donationData?.taxDeductible || [];
    const nonTaxDeductible = donationData?.nonTaxDeductible || [];
    const allDonations = [...taxDeductible, ...nonTaxDeductible];
    
    const donationRowsAsCommon: CommonRow[] = allDonations.map((donation: any) => ({
        id: donation.id,
        item: '',
        perClient: donation.perClient,
        adjustment: donation.adjustment,
        adjustments: donation.adjustments,
        lastYearFinal: donation.lastYearFinal
    }));
    
    if (allDonations.length === 0) {
        return null;
    }
    
    const amountDifference = calculateAmount(donationRowsAsCommon);
    const totalFinal = donationRowsAsCommon.reduce((sum: number, row: CommonRow) => 
        sum + (Number(row.perClient) || 0) + getAdjustmentTotal(row), 0);
    
    return buildAdjustmentLine({
        code,
        detailsPayload: detail,
        categoryLine: line,
        companyId: annualId,
        annualId,
        statement: line.statement as 'BS' | 'PL',
        currency: 'HKD',
        totalAdjustment: Math.abs(amountDifference),
        totalFinal,
        entry: baseEntry,
    });
}

function processCommissionPayment(code: string, detail: any, line: any, annualId: number, baseEntry: string) {
    const commissionData = extractRowsDetails(detail, undefined, 'commissionPayment') as any;
    const commissionPayments = commissionData?.commissionPayments || [];
    
    const commissionRowsAsCommon: CommonRow[] = commissionPayments.map((commission: any) => ({
        id: commission.id,
        item: '',
        perClient: commission.perClient,
        adjustment: commission.adjustment,
        adjustments: commission.adjustments,
        lastYearFinal: commission.lastYearFinal
    }));
    
    if (commissionPayments.length === 0) {
        return null;
    }
    
    const amountDifference = calculateAmount(commissionRowsAsCommon);
    const totalFinal = commissionRowsAsCommon.reduce((sum: number, row: CommonRow) => 
        sum + (Number(row.perClient) || 0) + getAdjustmentTotal(row), 0);
    
    return buildAdjustmentLine({
        code,
        detailsPayload: detail,
        categoryLine: line,
        companyId: annualId,
        annualId,
        statement: line.statement as 'BS' | 'PL',
        currency: 'HKD',
        totalAdjustment: Math.abs(amountDifference),
        totalFinal,
        entry: baseEntry,
    });
}

function processGainLossExchange(code: string, detail: any, line: any, annualId: number, baseEntry: string) {
    const exchangeData = extractRowsDetails(detail, undefined, 'gainLossExchange') as any;
    const gainExchange = exchangeData?.gainExchange || [];
    const lossExchange = exchangeData?.lossExchange || [];
    
    const gainExchangeRows: CommonRow[] = gainExchange.map((gain: any) => ({
        id: gain.id,
        item: '',
        perClient: gain.perClient,
        adjustment: gain.adjustment,
        lastYearFinal: gain.lastYearFinal
    }));
    
    const lossExchangeRows: CommonRow[] = lossExchange.map((loss: any) => ({
        id: loss.id,
        item: '',
        perClient: loss.perClient,
        adjustment: loss.adjustment,
        lastYearFinal: loss.lastYearFinal
    }));
    
    if (gainExchange.length === 0 && lossExchange.length === 0) {
        return null;
    }
    
    const gainAmount = calculateAmount(gainExchangeRows);
    const lossAmount = calculateAmount(lossExchangeRows);
    const amountDifference = gainAmount - lossAmount;
    const totalFinal = gainAmount + lossAmount;
    
    return buildAdjustmentLine({
        code,
        detailsPayload: detail,
        categoryLine: line,
        companyId: annualId,
        annualId,
        statement: line.statement as 'BS' | 'PL',
        currency: 'HKD',
        totalAdjustment: Math.abs(amountDifference),
        totalFinal,
        entry: baseEntry,
    });
}

function processGainLossDisposal(code: string, detail: any, line: any, annualId: number, baseEntry: string) {
    const disposalData = extractRowsDetails(detail, undefined, 'gainLossDisposal') as any;
    const allDisposalRows = [
        ...(disposalData?.locationOfPropertyDisRow || []),
        ...(disposalData?.commercialAllowanceDisRow || []),
        ...(disposalData?.industrialAllowanceDisRow || []),
        ...(disposalData?.furnitureFixtureDisRow || []),
        ...(disposalData?.computerHardSoftDisRow || []),
        ...(disposalData?.fullPayMotorDisRow || []),
        ...(disposalData?.hirePurchaseMotorDisRow || []),
        ...(disposalData?.machineryDisRow || []),
        ...(disposalData?.liftEscalatorDisRow || []),
        ...(disposalData?.equipmentDisRow || []),
        ...(disposalData?.subsidiaryRow || []),
        ...(disposalData?.associateRow || []),
        ...(disposalData?.intangibleRow || []),
        ...(disposalData?.taxiLicenseDisRow || []),
        ...(disposalData?.fullPayTaxiCabDisRow || []),
        ...(disposalData?.hirePurchaseTaxiCabDisRow || []),
        ...(disposalData?.trustsRow || []),
    ];
    
    const disposalRowsAsCommon: CommonRow[] = allDisposalRows.map((row: any) => ({
        id: row.id,
        item: '',
        perClient: row.perClient,
        adjustments: row.adjustments,
        lastYearFinal: row.lastYearFinal
    }));
    
    if (allDisposalRows.length === 0) {
        return null;
    }
    
    const amountDifference = calculateAmount(disposalRowsAsCommon);
    const totalFinal = disposalRowsAsCommon.reduce((sum: number, row: CommonRow) => 
        sum + (Number(row.perClient) || 0) + getAdjustmentTotal(row), 0);
    
    return buildAdjustmentLine({
        code,
        detailsPayload: detail,
        categoryLine: line,
        companyId: annualId,
        annualId,
        statement: line.statement as 'BS' | 'PL',
        currency: 'HKD',
        totalAdjustment: Math.abs(amountDifference),
        totalFinal,
        entry: baseEntry,
    });
}

function processDueFromToDirectors(code: string, detail: any, line: any, annualId: number, baseEntry: string) {
    const dirData = extractRowsDetails(detail, undefined, 'dueFromToDirectors') as any;
    const dueFromToDirRow = dirData?.dueFromToDirRow || [];

    if (dueFromToDirRow.length === 0) {
        return null;
    }

    const dirRowsAsCommon: CommonRow[] = dueFromToDirRow.map((r: any) => ({
        id: r.id,
        item: r.directorName || '',
        perClient: r.perClient,
        adjustment: r.adjustment,
        adjustments: r.adjustments,
        lastYearFinal: r.lastYearFinal,
    }));

    const amountDifference = calculateAmount(dirRowsAsCommon);
    const totalFinal = dirRowsAsCommon.reduce((sum: number, row: CommonRow) =>
        sum + (Number(row.perClient) || 0) + getAdjustmentTotal(row), 0);

    // 使用原始 detail 作为 detailsPayload，确保 dueFromToDirRow 结构完整保留
    const detailsPayload = detail && typeof detail === 'object' && detail.dueFromToDirRow
        ? detail
        : { dueFromToDirRow };

    return buildAdjustmentLine({
        code,
        detailsPayload,
        categoryLine: line,
        companyId: annualId,
        annualId,
        statement: line.statement as 'BS' | 'PL',
        currency: 'HKD',
        totalAdjustment: Math.abs(amountDifference),
        totalFinal,
        entry: baseEntry,
    });
}

function processDefault(code: string, detail: any, line: any, annualId: number, baseEntry: string) {
    const rows = extractRowsDetails(detail);
    
    // 使用 calculateAmount 只计算当前 adjustment 值，不包含历史 adjustments 数组
    const totalAdjustment = calculateAmount(rows);
    const totalFinal = rows.reduce((sum: number, row: any) => 
        sum + (Number(row.perClient) || 0) + (Number(row.adjustment) || 0), 0);
    
    // 直接使用原始 detail 数据（已经是 { commonRow: [...] } 格式）
    return buildAdjustmentLine({
        code,
        detailsPayload: detail,
        categoryLine: line,
        companyId: annualId,
        annualId,
        statement: line.statement as 'BS' | 'PL',
        currency: 'HKD',
        totalAdjustment: Math.abs(totalAdjustment),
        totalFinal,
        entry: baseEntry,
    });
}
